import ms from "ms";
import { NextPage } from "next";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useInterval } from "usehooks-ts";

import ActivityItem from "@/client/components/common/ActivityItem";
import CenterContainer from "@/client/components/common/Layout/CenterContainer";
import LeftSidebarBody from "@/client/components/common/Layout/LeftSidebar/LeftSidebarBody";
import LeftSidebarContainer from "@/client/components/common/Layout/LeftSidebar/LeftSidebarContainer";
import LeftSidebarHeader from "@/client/components/common/Layout/LeftSidebar/LeftSidebarHeader";
import Loading from "@/client/components/common/Loading";
import MetadataHead from "@/client/components/common/MetdataHead";
import { useAuth } from "@/client/components/common/AuthProvider";
import useIsMobile from "@/client/components/hooks/useIsMobile";
import { Filters } from "@/client/components/icons/filters";
import { ListActivityParams, listActivity } from "@/client/lib/api";
import { Platform } from "@/shared/platforms";
import { Activity, ActivityKind } from "@/shared/types/activity";

import ActivityFilter from "./ActivityFilter";

const META_TITLE = "Art Blocks - View NFT activity";
const META_DESCRIPTION =
  "Discover, filter and watch NFT sales and listings activity across verified generative art collections from Art Blocks to Bright Moments.";

const LIMIT = 20;
const REFRESH_INTERVAL_MS = ms("10s");

const ActivityPage: NextPage = () => {
  const { address: currentUserAddress } = useAuth();
  const { isMobile } = useIsMobile();

  const [kind, setKind] = useState<ActivityKind>("ask");
  const [priceMin, setPriceMin] = useState<number>();
  const [priceMax, setPriceMax] = useState<number>();
  const [platform, setPlatform] = useState<Platform>();
  const [filtersOpen, setFiltersOpen] = useState<boolean>(true);
  const [onlySansaListings, setOnlySansaListings] = useState<boolean>(false);
  const [onlyNotableAddresses, setOnlyNotableAddresses] =
    useState<boolean>(false);
  const [onlyRelistingListings, setOnlyRelistingListings] =
    useState<boolean>(false);
  const [onlyNewFloorListings, setOnlyNewFloorListings] =
    useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>();
  const [activities, setActivities] = useState<Activity[]>();

  useEffect(() => {
    fetchInitialActivity();
  }, [
    kind,
    priceMin,
    priceMax,
    platform,
    onlySansaListings,
    onlyNewFloorListings,
    onlyNotableAddresses,
    onlyRelistingListings,
  ]);

  useEffect(() => {
    setFiltersOpen(!isMobile);
  }, [isMobile]);

  useInterval(() => {
    fetchLatestActivity();
  }, REFRESH_INTERVAL_MS);

  const fetchInitialActivity = async () => {
    setLoading(true);
    const response = await fetchActivity();
    setActivities(response);
    setHasMore(response?.length === LIMIT);
    setLoading(false);
  };

  const fetchLatestActivity = async () => {
    if (!activities?.length) return;

    const response = await fetchActivity({
      occurredAtAfter: activities?.[0]?.occurredAt,
    });

    setActivities([...response, ...activities]);
  };

  const fetchMoreActivity = async () => {
    if (!activities?.length) return;

    const response = await fetchActivity({
      occurredAtBefore: activities?.[activities.length - 1]?.occurredAt,
    });

    setActivities([...activities, ...response]);
    setHasMore(response?.length === LIMIT);
  };

  const fetchActivity = async ({
    occurredAtAfter,
    occurredAtBefore,
  }: {
    occurredAtAfter?: Date;
    occurredAtBefore?: Date;
  } = {}) => {
    let params = buildQueryParams({ occurredAtAfter, occurredAtBefore });
    let response = await listActivity(params);
    return response;
  };

  const buildQueryParams = ({
    occurredAtAfter,
    occurredAtBefore,
  }: {
    occurredAtAfter?: Date;
    occurredAtBefore?: Date;
  }) => {
    const params = { limit: LIMIT } as ListActivityParams;
    params.filters = { kind };

    if (occurredAtAfter) params.filters.occurredAtAfter = occurredAtAfter;
    if (occurredAtBefore) params.filters.occurredAtBefore = occurredAtBefore;
    if (priceMin) params.filters.priceMin = priceMin;
    if (priceMax) params.filters.priceMax = priceMax;
    if (platform) params.filters.platform = platform;
    if (onlySansaListings) params.filters.onlySansaListings = onlySansaListings;
    if (onlyNotableAddresses)
      params.filters.onlyNotableAddresses = onlyNotableAddresses;
    if (onlyRelistingListings)
      params.filters.onlyRelistingListings = onlyRelistingListings;
    if (onlyNewFloorListings)
      params.filters.onlyNewFloorListings = onlyNewFloorListings;
    return params;
  };

  return (
    <>
      <MetadataHead title={META_TITLE} description={META_DESCRIPTION} />
      <div className="flex">
        <LeftSidebarContainer open={filtersOpen}>
          <LeftSidebarHeader
            open={filtersOpen}
            closedIcon={<Filters size={32} />}
            setOpen={() => setFiltersOpen((prev: boolean) => !prev)}
          />
          <LeftSidebarBody open={filtersOpen}>
            <ActivityFilter
              kind={kind}
              setKind={setKind}
              onlySansaListings={onlySansaListings}
              toggleOnlySansaListings={() =>
                setOnlySansaListings((prev: boolean) => !prev)
              }
              onlyRelistingListings={onlyRelistingListings}
              toggleOnlyRelistingListings={() =>
                setOnlyRelistingListings((prev: boolean) => !prev)
              }
              onlyNewFloorListings={onlyNewFloorListings}
              toggleOnlyNewFloorListings={() =>
                setOnlyNewFloorListings((prev: boolean) => !prev)
              }
              onlyNotableAddresses={onlyNotableAddresses}
              toggleOnlyNotableAddresses={() =>
                setOnlyNotableAddresses((prev: boolean) => !prev)
              }
              platform={platform}
              setPlatform={setPlatform}
              priceMin={priceMin}
              setPriceMin={setPriceMin}
              priceMax={priceMax}
              setPriceMax={setPriceMax}
            />
          </LeftSidebarBody>
        </LeftSidebarContainer>
        <CenterContainer leftOpen={filtersOpen}>
          <div className="dark:text-white">
            <div className="pt-12">
              <h1 className="px-8 sm:px-10 md:px-12 text-3xl sm:text-4xl tracking-tight font-medium">
                Activity
              </h1>
            </div>
            <div className="px-8 pt-6 md:hidden">
              <button
                onClick={() => setFiltersOpen(true)}
                className="border-2 dark:border-neutral-700 dark:text-white w-full flex items-center gap-2 p-4 rounded-xs"
              >
                <Filters size={24} />
                Filters
              </button>
            </div>
            <div>
              {loading ? (
                <div className="h-96 flex flex-col items-center justify-center">
                  <Loading />
                </div>
              ) : (
                <>
                  {!activities?.length ? (
                    <EmptyState />
                  ) : (
                    <InfiniteScroll
                      dataLength={activities.length}
                      next={fetchMoreActivity}
                      hasMore={hasMore === true}
                      loader={
                        <p className="mx-auto mt-8 text-sm text-center text-neutral-500">
                          Loading more...
                        </p>
                      }
                      endMessage={""}
                    >
                      <div className="mt-8 md:mt-0 md:pt-8 px-8 sm:px-10 md:p-12 flex flex-col gap-4 sm:gap-8">
                        <div className="bg-neutral-100 xl:flex rounded-xs py-4 mt-8 md:mt-0 dark:text-white dark:bg-neutral-800 gap-2 lg:gap-8 xl:gap-12 px-8 font-medium flex items-center justify-between">
                          <div className="w-32 text-sm ">Type</div>
                          <div className="text-sm flex items-center flex-grow gap-2">
                            Token
                          </div>
                          <div className="w-24 text-right text-sm ">Price</div>
                          <div className="w-24 text-right   text-sm hidden lg:block">
                            From
                          </div>
                          <div
                            className={`${
                              kind === "ask" ? "w-44" : "w-24"
                            } text-right text-sm`}
                          >
                            {kind === "ask" ? "Actions" : "To"}
                          </div>
                          <div className="w-32 hidden 2xl:block text-right text-sm">
                            Time
                          </div>
                        </div>

                        {activities?.map((activityItem: Activity) => {
                          return (
                            <ActivityItem
                              key={activityItem._id}
                              eventType={activityItem.kind}
                              tokenName={activityItem?.asset?.name}
                              tokenId={activityItem?.tokenId}
                              contractAddress={activityItem?.contractAddress}
                              tokenImage={
                                activityItem?.asset?.imageThumbnailUrl
                              }
                              priceCurrencySymbol={
                                activityItem.price?.currency?.symbol
                              }
                              price={activityItem.price?.amount?.decimal}
                              createdAt={
                                activityItem?.occurredAt
                                  ? new Date(activityItem?.occurredAt)
                                  : undefined
                              }
                              fromAddress={activityItem?.fromAddress}
                              fromAddressWalletInfo={
                                activityItem?.fromAddressWalletInfo
                              }
                              toAddress={activityItem?.toAddress}
                              toAddressWalletInfo={
                                activityItem?.toAddressWalletInfo
                              }
                              collectionId={activityItem?.collectionId}
                              collectionName={
                                activityItem?.assetCollection?.name
                              }
                              currentUserAddress={currentUserAddress}
                              txHash={activityItem.transactionHash}
                              newFloor={
                                !!activityItem.collectionFloorPriceReset
                                  ?.newPriceInEth
                              }
                              assetRelistingCount={
                                activityItem?.assetRelistingCount
                              }
                            />
                          );
                        })}
                      </div>
                    </InfiniteScroll>
                  )}
                </>
              )}
            </div>
          </div>
        </CenterContainer>
      </div>
    </>
  );
};

const EmptyState = () => {
  return (
    <div className="h-96 flex flex-col items-center justify-center">
      <p className="text-neutral-500">No assets with these filters</p>
    </div>
  );
};

export default ActivityPage;
